import React, { useEffect, useMemo, useState, useCallback } from "react";
import formOptions from "../../config/formOptions";
import {
  Box,
  Typography,
  Chip,
  Paper,
  Button,
  useTheme,
  Snackbar,
  Alert,
  Checkbox,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { GraphQLClient } from "graphql-request";
import { graphqlEndpoint } from "../../config";
import { tokens } from "../../theme/tokens";
import LabelSelector from "../../components/LabelSelector";
import { useSettings } from "../../context/SettingsProvider";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Header from "../../components/Header";
import ActionButton from "../../components/ActionButton";
import AdminUserSelect from "../../components/AdminUserSelect";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

import { READ_ALL_TASKS } from "../../graph/tasks/queries";
import { READ_PROJECTS_FILTER } from "../../graph/projects/queries";
import { UPDATE_TASK_STATUS } from "../../graph/tasks/mutations";

import { useTenant } from "../../context/TenantContext";

const client = new GraphQLClient(graphqlEndpoint);

const KanbanBoard = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { tenantId } = useTenant();

  const { showComplete } = useSettings();

  const STATUS_COLUMNS = {
    INBOX: "Inbox",
    NEXT_ACTION: "Next Action",
    SCHEDULED: "Scheduled",
    IN_PROGRESS: "In Progress",
    BLOCKED: "Blocked",
    ...(showComplete && { DONE: "Done" }),
    CANCELLED: "Cancelled",
  };

  const [selectedLabels, setSelectedLabels] = useState([]);
  const [selectedAdminId, setSelectedAdminId] = useState("");

  const [lastMovedTask, setLastMovedTask] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [tasksByStatus, setTasksByStatus] = useState({
    INBOX: [],
    NEXT_ACTION: [],
    SCHEDULED: [],
    IN_PROGRESS: [],
    BLOCKED: [],
    DONE: [],
    CANCELLED: [],
  });

  const updateTaskStatus = async (id, status) => {
    try {
      const input = { id, status };
      await client.request(UPDATE_TASK_STATUS, { input });

      setSnackbar({
        open: true,
        message: `Task updated to "${status}"`,
        severity: "success",
      });
    } catch (error) {
      console.error(
        "Failed to update task status:",
        error?.response || error?.message
      );
      setSnackbar({
        open: true,
        message: "Failed to update task",
        severity: "error",
      });
    }
  };

  const handleDragEnd = (result) => {
    const { destination, source } = result;
    if (!destination) return;

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const sourceCol = source.droppableId;
    const destCol = destination.droppableId;

    const sourceTasks = [...(tasksByStatus[sourceCol] || [])];
    const destTasks = [...(tasksByStatus[destCol] || [])];

    const [movedTask] = sourceTasks.splice(source.index, 1);
    movedTask.status = destCol;

    if (sourceCol === destCol) {
      sourceTasks.splice(destination.index, 0, movedTask);
      setTasksByStatus((prev) => ({
        ...prev,
        [sourceCol]: sourceTasks,
      }));
    } else {
      destTasks.splice(destination.index, 0, movedTask);
      setTasksByStatus((prev) => ({
        ...prev,
        [sourceCol]: sourceTasks,
        [destCol]: destTasks,
      }));
      updateTaskStatus(movedTask.id, destCol);
    }
  };

  const fetchData = useCallback(async () => {
    if (!tenantId) {
      setTasksByStatus({
        INBOX: [],
        NEXT_ACTION: [],
        SCHEDULED: [],
        IN_PROGRESS: [],
        BLOCKED: [],
        DONE: [],
        CANCELLED: [],
      });
      return;
    }

    try {
      // 1) Get SOP project IDs for the selected tenant
      const sopData = await client.request(READ_PROJECTS_FILTER, {
        filter: {
          tenantId,
          isSOPTemplate: true,
        },
      });

      const sopProjectIds = (sopData.readProjectsFilter || []).map((p) => p.id);

      // 2) Get all tenant-scoped tasks
      const { readAllTasks } = await client.request(READ_ALL_TASKS, {
        tenantId,
        projectId: null,
        assignedTenantUserId: null,
        accountableRoleId: null,
      });

      // 3) Exclude tasks that belong to SOP template projects
      const filteredTasks = (readAllTasks || []).filter(
        (task) => !sopProjectIds.includes(task.projectId)
      );

      // 4) Group by status
      const grouped = {
        INBOX: [],
        NEXT_ACTION: [],
        SCHEDULED: [],
        IN_PROGRESS: [],
        BLOCKED: [],
        DONE: [],
        CANCELLED: [],
      };

      const validStatuses = Object.keys(grouped);

      for (const task of filteredTasks) {
        const status = task.status;
        if (validStatuses.includes(status)) {
          grouped[status].push(task);
        } else {
          grouped.INBOX.push(task);
        }
      }

      // Optional: sort within each column by sequence if present
      Object.keys(grouped).forEach((key) => {
        grouped[key] = grouped[key].sort((a, b) => {
          const aSeq = a.sequence || "";
          const bSeq = b.sequence || "";
          return aSeq.localeCompare(bSeq);
        });
      });

      setTasksByStatus(grouped);
    } catch (err) {
      console.error("Error loading Kanban data:", err);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const visibleStatuses = useMemo(() => Object.entries(STATUS_COLUMNS), [STATUS_COLUMNS]);

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        flexWrap="wrap"
        mb={2}
      >
        <Header title="KANBAN" subtitle="View and organise tasks" />

        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          <ActionButton
            tone="gold"
            onClick={() =>
              navigate("/createTask", {
                state: { redirectPath: "/kanban" },
              })
            }
            disabled={!tenantId}
          >
            + Task
          </ActionButton>

          <Box minWidth={220}>
            <AdminUserSelect
              selectedAdmin={selectedAdminId}
              setFieldValue={(_, v) => setSelectedAdminId(v)}
            />
          </Box>
        </Box>
      </Box>

      <Box mb={2}>
        <LabelSelector
          selectedLabels={selectedLabels}
          setFieldValue={(_, value) => setSelectedLabels(value)}
          error={null}
          touched={false}
        />
      </Box>

      {!tenantId ? (
        <Box mt={4}>
          <Typography variant="h6">
            Select a tenant in the top bar to view the Kanban board.
          </Typography>
        </Box>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Box
            display="flex"
            gap={2}
            p={2}
            overflow="auto"
            backgroundColor="background.paper"
            borderRadius="8px"
            boxShadow={1}
            minHeight="60vh"
          >
            {visibleStatuses.map(([key, label]) => {
              const tasks = (tasksByStatus[key] || []).filter((task) => {
                const matchesLabels =
                  selectedLabels.length === 0 ||
                  (task.labels || []).some((l) => selectedLabels.includes(l));

                const matchesAdmin =
                  !selectedAdminId ||
                  task.assignedTenantUserId === selectedAdminId;

                return matchesLabels && matchesAdmin;
              });

              return (
                <Droppable droppableId={key} key={key}>
                  {(provided) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      flex={1}
                      minWidth={260}
                      maxWidth="100%"
                      backgroundColor="background.paper"
                      borderRadius="8px"
                      boxShadow={2}
                      p={2}
                      sx={{
                        flexBasis: 0,
                        border: `1px solid ${colors.surface.border}`,
                      }}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={2}
                        pb={1}
                        borderBottom={`2px solid ${colors.scalpelTeal[400]}`}
                      >
                        <Typography
                          variant="h6"
                          sx={{ color: colors.grey[200], fontWeight: 600 }}
                        >
                          {label} ({tasks.length})
                        </Typography>
                      </Box>

                      {tasks.map((task, index) => (
                        <Draggable draggableId={task.id} index={index} key={task.id}>
                          {(provided) => (
                            <Paper
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              sx={{
                                position: "relative",
                                p: 2,
                                mb: 2,
                                borderRadius: "8px",
                                backgroundColor: colors.surface.card,
                                border: `1px solid ${colors.surface.border}`,
                                transition:
                                  "transform 0.1s ease-in-out, box-shadow 0.2s",
                                "&:hover": {
                                  transform: "scale(1.02)",
                                  boxShadow: `0 4px 20px ${colors.scalpelTeal[700]}`,
                                },
                              }}
                              elevation={3}
                            >
                              <Box display="flex" alignItems="center" gap={1}>
                                {task.status !== "DONE" && (
                                  <Tooltip title="Mark as done" arrow>
                                    <Checkbox
                                      icon={<CheckCircleOutlineIcon />}
                                      checkedIcon={<CheckCircleIcon />}
                                      sx={{
                                        color: colors.scalpelTeal[300],
                                        "&.Mui-checked": {
                                          color: colors.scalpelTeal[400],
                                        },
                                        p: 0.5,
                                      }}
                                      onChange={() => {
                                        const originalStatus = task.status;
                                        updateTaskStatus(task.id, "DONE");

                                        setTasksByStatus((prev) => {
                                          const updated = { ...prev };
                                          updated[originalStatus] =
                                            updated[originalStatus].filter(
                                              (t) => t.id !== task.id
                                            );
                                          updated.DONE = [
                                            ...updated.DONE,
                                            { ...task, status: "DONE" },
                                          ];
                                          return updated;
                                        });

                                        setLastMovedTask({
                                          ...task,
                                          status: originalStatus,
                                        });

                                        setSnackbar({
                                          open: true,
                                          message: "Task marked as done",
                                          severity: "success",
                                        });
                                      }}
                                    />
                                  </Tooltip>
                                )}

                                <Tooltip title="Edit Task" arrow>
                                  <Typography
                                    fontWeight="bold"
                                    fontSize="0.95rem"
                                    sx={{
                                      cursor: "pointer",
                                      "&:hover": {
                                        color: colors.scalpelTeal[300],
                                      },
                                    }}
                                    onClick={() =>
                                      navigate(`/tasks/edit/${task.id}`, {
                                        state: { redirectPath: "/kanban" },
                                      })
                                    }
                                  >
                                    {task.title}
                                  </Typography>
                                </Tooltip>
                              </Box>

                              <Box mt={1} display="flex" flexWrap="wrap" gap={1}>
                                {(task.labels || []).map((labelText) => {
                                  const labelDef = formOptions.labelOptions?.find(
                                    (l) => l.value === labelText
                                  );

                                  return (
                                    <Chip
                                      key={labelText}
                                      label={labelText}
                                      size="small"
                                      style={{
                                        backgroundColor: labelDef?.color || "#723232",
                                        color: colors.surface.raised,
                                        fontWeight: "bold",
                                      }}
                                    />
                                  );
                                })}
                              </Box>
                            </Paper>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
              );
            })}
          </Box>
        </DragDropContext>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          action={
            lastMovedTask ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  updateTaskStatus(lastMovedTask.id, lastMovedTask.status);

                  setTasksByStatus((prev) => {
                    const updated = { ...prev };
                    updated.DONE = updated.DONE.filter(
                      (t) => t.id !== lastMovedTask.id
                    );
                    updated[lastMovedTask.status] = [
                      ...updated[lastMovedTask.status],
                      lastMovedTask,
                    ];
                    return updated;
                  });

                  setSnackbar({ open: false, message: "", severity: "info" });
                  setLastMovedTask(null);
                }}
              >
                UNDO
              </Button>
            ) : null
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default KanbanBoard;